import { useQuery } from "@tanstack/react-query";
import { Link } from "wouter";
import { MapPin, Calendar, Tag } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { fetchDestinations, Destination } from "@/services/destinationService";
import SEO from "@/components/SEO";
import PageBreadcrumbs from "@/components/PageBreadcrumbs";
import JsonLd from "@/components/JsonLd";

// Define a simple Region interface
interface Region {
  id: string;
  name: string;
}

// Add fallback destinations that are available in CityTemplate
const fallbackDestinations: Destination[] = [
  {
    id: "ephesus",
    name: "Ephesus",
    slug: "ephesus",
    region: "Aegean",
    description: "One of the best-preserved ancient cities in the Mediterranean, featuring impressive structures from the Roman period.",
    content: "Ephesus (Efes in Turkish) is one of the most significant ancient sites in Turkey and among the best-preserved Greco-Roman ruins in the Mediterranean region.",
    imageUrl: "/images/destinations/ephesus.jpg",
    activities: ["Archaeological Sites", "Historical Tours", "Photography", "Cultural Heritage", "Museum Visits"],
    mapLocation: {
      latitude: 37.9398,
      longitude: 27.3419
    }
  },
  {
    id: "pamukkale",
    name: "Pamukkale",
    slug: "pamukkale",
    region: "Aegean",
    description: "Famous for its dazzling white calcium terraces (travertines) and the ancient city of Hierapolis situated above them.",
    content: "Pamukkale, meaning \"Cotton Castle\" in Turkish, is a natural wonder that has drawn visitors for thousands of years.",
    imageUrl: "/images/destinations/pamukkale.jpg",
    activities: ["Thermal Springs", "Historical Ruins", "Photography", "Swimming", "Natural Wonders"],
    mapLocation: {
      latitude: 37.9249,
      longitude: 29.1203
    }
  },
  {
    id: "fethiye",
    name: "Fethiye",
    slug: "fethiye",
    region: "Mediterranean",
    description: "A beautiful coastal town with breathtaking beaches, ancient Lycian rock tombs, and the famous Blue Lagoon at Ölüdeniz.",
    content: "Fethiye is a stunning coastal gem on Turkey's southwestern Turquoise Coast, where the Mediterranean meets the Aegean Sea.",
    imageUrl: "/images/destinations/fethiye.jpg",
    activities: ["Beach Activities", "Paragliding", "Boat Tours", "Historical Sites", "Hiking"],
    mapLocation: {
      latitude: 36.6211,
      longitude: 29.1161
    }
  },
  {
    id: "izmir",
    name: "Izmir",
    slug: "izmir",
    region: "Aegean",
    description: "Turkey's third-largest city with a relaxed Mediterranean atmosphere, rich history, and excellent coastal access.",
    content: "Izmir, Turkey's third-largest city, combines cosmopolitan sophistication with ancient history and relaxed Mediterranean charm.",
    imageUrl: "/images/destinations/izmir.jpg",
    activities: ["City Sightseeing", "Cultural Experiences", "Shopping", "Culinary Exploration", "Day Trips"],
    mapLocation: {
      latitude: 38.4237,
      longitude: 27.1428
    }
  },
  {
    id: "troy",
    name: "Troy",
    slug: "troy",
    region: "Marmara",
    description: "The legendary ancient city featured in Homer's Iliad, with archaeological remains spanning 4,000 years of history.",
    content: "Troy (Truva in Turkish) is one of the world's most famous archaeological sites, made legendary through Homer's epic poems.",
    imageUrl: "/images/destinations/troy.jpg",
    activities: ["Archaeological Exploration", "Historical Tours", "Museum Visits", "Photography", "Cultural Heritage"],
    mapLocation: {
      latitude: 39.9571,
      longitude: 26.2391
    }
  },
  {
    id: "konya",
    name: "Konya",
    slug: "konya",
    region: "Central Anatolia",
    description: "A spiritual center known for Rumi's mausoleum and as the home of the whirling dervishes.",
    content: "Konya, one of Turkey's oldest continuously inhabited cities, holds profound cultural and spiritual significance.",
    imageUrl: "/images/destinations/konya.jpg",
    activities: ["Spiritual Tourism", "Cultural Heritage", "Museum Visits", "Historical Sites", "Traditional Food"],
    mapLocation: {
      latitude: 37.8719,
      longitude: 32.4844
    }
  }
];

// Group destinations by region to create region list
const useRegions = (destinations: Destination[] | undefined): Region[] => {
  if (!destinations) return [];
  
  const regionMap = new Map<string, string>();
  
  destinations.forEach(destination => {
    if (destination.region) {
      regionMap.set(destination.region, destination.region);
    }
  });
  
  return Array.from(regionMap.entries()).map(([id, name]) => ({ id, name }));
};

// Ensure each city appears only once within a region
const getUniqueDestinations = (destinations: Destination[] | undefined): Destination[] => {
  if (!destinations) return [];
  
  // Group by region
  const regionDestinations = new Map<string, Map<string, Destination>>();
  
  destinations.forEach(destination => {
    if (!regionDestinations.has(destination.region)) {
      regionDestinations.set(destination.region, new Map<string, Destination>());
    }
    
    // Only add if this city name doesn't already exist in this region
    const regionCities = regionDestinations.get(destination.region)!;
    if (!regionCities.has(destination.name)) {
      regionCities.set(destination.name, destination);
    }
  });
  
  // Flatten the map back to an array
  const result: Destination[] = [];
  regionDestinations.forEach(cityMap => {
    cityMap.forEach(destination => {
      result.push(destination);
    });
  });
  
  return result;
};

// Add Ankara if it doesn't exist in Central Anatolia
const ensureAnkaraExists = (destinations: Destination[]): Destination[] => {
  // Check if Ankara already exists in Central Anatolia
  const ankaraExists = destinations.some(
    dest => dest.name === "Ankara" && dest.region === "Central Anatolia"
  );
  
  if (!ankaraExists) {
    // Add Ankara to Central Anatolia
    const ankara: Destination = {
      id: "ankara",
      name: "Ankara",
      slug: "ankara",
      region: "Central Anatolia",
      description: "Turkey's capital city with important historical sites and modern infrastructure.",
      content: "Ankara is the capital of Turkey and the country's second-largest city. It's a modern city with a rich history, home to government buildings, universities, and many foreign embassies. Key attractions include the Anıtkabir (Atatürk's Mausoleum), the Museum of Anatolian Civilizations, and Ankara Castle.",
      imageUrl: "https://images.unsplash.com/photo-1597806999047-20c1f128b8fb?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=800&q=80",
      isFeatured: false,
      activities: ["Historical Sightseeing", "Museums", "Modern Architecture"],
      mapLocation: {
        latitude: 39.9334,
        longitude: 32.8597
      }
    };
    return [...destinations, ankara];
  }
  
  return destinations;
};

// Add the fallback destinations to the list
const addFallbackDestinations = (destinations: Destination[]): Destination[] => {
  // Create a Set of existing destination slugs for quick lookup
  const existingSlugs = new Set(destinations.map(dest => dest.slug));
  
  // Add fallback destinations that aren't already in the list
  const newDestinations = [...destinations];
  
  fallbackDestinations.forEach(fallbackDest => {
    if (!existingSlugs.has(fallbackDest.slug)) {
      newDestinations.push(fallbackDest);
      existingSlugs.add(fallbackDest.slug);
    }
  });
  
  return newDestinations;
};

// Add this map of cities to their correct images
const cityImageMap: { [key: string]: string } = {
  'Istanbul': 'https://images.unsplash.com/photo-1541432901042-2d8bd64b4a9b?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
  'Antalya': "/images/destinations/antalya.jpg",
  'Cappadocia': "/images/destinations/cappadocia.jpg",
  'Pamukkale': "/images/destinations/pamukkale.jpg",
  'Bodrum': 'https://images.unsplash.com/photo-1524231757912-21f4fe3a7200?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
  'Ephesus': "/images/destinations/ephesus.jpg",
  'Ankara': "/images/destinations/ankara.jpg",
  'Fethiye': "/images/destinations/fethiye.jpg",
  'Izmir': "/images/destinations/izmir.jpg",
  'Troy': "/images/destinations/troy.jpg",
  'Konya': "/images/destinations/konya.jpg",
  'Alanya': 'https://images.unsplash.com/photo-1513546493312-0066d7de3fd2?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
  'Marmaris': 'https://images.unsplash.com/photo-1583254130747-04d659241ca2?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
};

export default function Destinations() {
  const { data: apiDestinations, isLoading, error } = useQuery<Destination[]>({
    queryKey: ["destinations"],
    queryFn: fetchDestinations
  });
  
  // Process destinations to remove duplicates, ensure Ankara exists, and add fallback destinations
  const destinations = apiDestinations 
    ? addFallbackDestinations(ensureAnkaraExists(getUniqueDestinations(apiDestinations)))
    : fallbackDestinations; // Use fallback destinations if API fails

  const regions = useRegions(destinations);

  if (error) {
    return (
      <div className="container mx-auto px-4 py-10">
        <SEO 
          title="Destinations - Error" 
          description="An error occurred while loading destinations. Please try again later."
          canonicalUrl="/destinations"
        />
        <h1 className="text-3xl md:text-4xl font-bold text-center mb-8">Destinations</h1>
        <div className="flex justify-center items-center h-60">
          <p className="text-red-500">Failed to load destinations. Please try again later.</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <SEO
        title="13 Best Places to Visit in Turkey (2026 Destination Guide)"
        description="Explore Turkey's top destinations: Istanbul, Cappadocia, Pamukkale, Antalya, Bodrum, Ephesus and more. Photos, travel tips, and what makes each one special."
        canonicalUrl="/destinations"
        keywords="best places to visit in turkey 2026, turkey destinations, top turkey attractions, istanbul, cappadocia, antalya, bodrum, pamukkale, ephesus, fethiye, turkey holiday destinations, where to go in turkey"
      />
      <JsonLd data={{
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        "name": "Turkey Travel Destinations",
        "description": "Explore Turkey's top destinations including Istanbul, Cappadocia, Pamukkale, Antalya, Bodrum, Ephesus and more.",
        "url": "https://turkeyvacationguide.com/destinations",
        "isPartOf": {
          "@type": "WebSite",
          "name": "Turkey Vacation Guide",
          "url": "https://turkeyvacationguide.com"
        }
      }} />
      <PageBreadcrumbs crumbs={[{ label: "Destinations" }]} />

      <section className="bg-[#F5F5F5] py-12">
        <div className="container mx-auto px-4">
          <h1 className="text-3xl md:text-4xl font-bold text-center mb-8">Discover Turkey's Destinations</h1>
          
          <div className="mb-10">
            <h2 className="text-xl md:text-2xl font-bold mb-4">Browse by Region</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-2 md:gap-4">
              {isLoading ? (
                Array(6).fill(0).map((_, i) => (
                  <Skeleton key={i} className="h-10 w-full" />
                ))
              ) : (
                regions?.map((region) => (
                  <a 
                    key={region.id}
                    href={`#region-${region.id}`}
                    className="bg-white p-2 rounded text-center hover:bg-[#30D5C8] hover:text-white transition duration-200"
                  >
                    <span className="font-poppins font-medium">{region.name}</span>
                  </a>
                ))
              )}
            </div>
          </div>
          
          {isLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
              {Array(6).fill(0).map((_, i) => (
                <Card key={i} className="overflow-hidden">
                  <Skeleton className="h-48 w-full" />
                  <CardContent className="p-5">
                    <Skeleton className="h-6 w-3/4 mb-4" />
                    <Skeleton className="h-4 w-full mb-2" />
                    <Skeleton className="h-4 w-full mb-2" />
                    <Skeleton className="h-4 w-3/4" />
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            regions?.map((region) => (
              <div key={region.id} id={`region-${region.id}`} className="mb-12">
                <h2 className="text-2xl font-bold mb-6 pb-2 border-b border-gray-200">{region.name}</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
                  {destinations
                    ?.filter(d => d.region === region.id)
                    .map((destination) => (
                      <Card key={destination.id} className="bg-white rounded-lg overflow-hidden shadow-md group hover:shadow-lg transition duration-200">
                        <div className="relative h-48 md:h-56 overflow-hidden">
                          <img
                            src={cityImageMap[destination.name] || destination.imageUrl || "https://images.unsplash.com/photo-1524231757912-21f4fe3a7200?auto=format&fit=crop&w=800&q=80"}
                            alt={destination.name}
                            loading="lazy"
                            width={800}
                            height={450}
                            className="w-full h-full object-cover group-hover:scale-105 transition duration-500"
                            onError={(e) => {
                              // Fallback image if the original fails to load
                              // Use the mapped image if available, otherwise a generic fallback
                              (e.target as HTMLImageElement).src = cityImageMap[destination.name] || "https://images.unsplash.com/photo-1524231757912-21f4fe3a7200?auto=format&fit=crop&w=800&q=80";
                            }}
                          />
                          <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black to-transparent">
                            <h3 className="text-xl font-bold text-white">{destination.name}</h3>
                          </div>
                        </div>
                        <CardContent className="p-5">
                          <p className="text-gray-600 mb-4">{destination.description}</p>
                          <div className="flex items-center justify-between">
                            <div className="flex space-x-2 flex-wrap">
                              {destination.activities && destination.activities.slice(0, 2).map((activity, index) => (
                                <span 
                                  key={index}
                                  className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800 mr-1 mb-1"
                                >
                                  <Tag size={12} className="mr-1" />
                                  {activity}
                                </span>
                              ))}
                            </div>
                            <Link href={`/destinations/${destination.slug}`}>
                              <a className="text-[#30D5C8] hover:text-[#E30A17] font-medium">
                                View Details
                              </a>
                            </Link>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                </div>
              </div>
            ))
          )}
        </div>
      </section>
    </>
  );
}
